import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// --- Interfaces ---
interface EventDetails {
  eventName: string;
  eventStartDate: string | null;
  eventEndDate: string | null;
  includeCoverPage: boolean;
  includeThankYouPage: boolean;
}

interface DetailItem {
  selected: boolean;
  questionText: string;
  answerText: string;
}

interface ComplianceItem {
  selected: boolean;
  simpleSelection: boolean;
  name: string;
  currency: string | null;
  amount: string | null;
  exchangeRate: string | null;
  amountUsd: string | null;
}

interface ReviewData {
  eventDetails?: EventDetails;
  otherDetails?: { details: DetailItem[] };
  compliance?: { flexible: boolean; items: ComplianceItem[] };
  sourcingNeeds?: { [key: string]: { label: string; items: DetailItem[] } };
}

@Injectable({
  providedIn: 'root',
})
export class ClientPdfGenService {
  private pageTemplate = {
    companyLogo: null as string | null,
  };

  constructor() {
    this.loadTemplates(); // load logo on service init
  }

  private getBase64FromUrl(url: string): Promise<string> {
    return fetch(url)
      .then((response) => response.blob())
      .then(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      );
  }

  private async loadTemplates() {
    try {
      // Production-compatible public folder path
      const logoUrl = window.location.origin + '/Company_Logo.png';
      const base64 = await this.getBase64FromUrl(logoUrl);
      if (base64) this.pageTemplate.companyLogo = base64;
    } catch (error) {
      console.error('Failed to load company logo for PDF:', error);
    }
  }

  generateEventPdf(reviewData: ReviewData): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        // Ensure logo is loaded before PDF generation
        if (!this.pageTemplate.companyLogo) {
          await this.loadTemplates();
        }

        const doc = new jsPDF();
        const pageMargin = 15;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        let hasContentPage = false;

        const { eventDetails, otherDetails, compliance, sourcingNeeds } =
          reviewData;

        // --- Cover Page ---
        if (eventDetails?.includeCoverPage) {
          doc.setFillColor(255, 255, 255);
          doc.rect(0, 0, pageWidth, pageHeight, 'F');

          const eventName = String(eventDetails.eventName || 'Event');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(40);
          const maxWidth = pageWidth - pageMargin * 2;
          const eventNameLines = doc.splitTextToSize(eventName, maxWidth);
          const textY = 100;
          doc.text(eventNameLines, pageWidth / 2, textY, { align: 'center' });

          const eventDate = String(eventDetails.eventStartDate || '');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(28);
          doc.text(eventDate, pageWidth / 2, textY + 80, { align: 'center' });

          // Logo
          if (this.pageTemplate.companyLogo) {
            const logoWidth = 100;
            const logoHeight = 45;
            const logoX = (pageWidth - logoWidth) / 2;
            const logoY = pageHeight - logoHeight - pageMargin * 2;

            const format = this.pageTemplate.companyLogo.startsWith(
              'data:image/png'
            )
              ? 'PNG'
              : 'JPEG';

            doc.addImage(
              this.pageTemplate.companyLogo,
              format,
              logoX,
              logoY,
              logoWidth,
              logoHeight
            );
          }
        }

        // --- Content Tables ---
        const addContentTables = () => {
          if (!hasContentPage) {
            if (eventDetails?.includeCoverPage) {
              doc.addPage();
            }
            hasContentPage = true;
          }

          let finalY = pageMargin;
          doc.setTextColor('#000000');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(20);
          doc.text('Event Details Summary', pageMargin, finalY);
          finalY += 12;

          const checkPageBreak = (requiredHeight: number) => {
            if (finalY + requiredHeight > pageHeight - pageMargin) {
              doc.addPage();
              finalY = pageMargin;
            }
          };

          if (eventDetails) {
            autoTable(doc, {
              startY: finalY,
              head: [['Field', 'Value']],
              body: [
                ['Event Name', String(eventDetails.eventName || '')],
                [
                  'Event Start Date',
                  String(eventDetails.eventStartDate || 'N/A'),
                ],
                ['Event End Date', String(eventDetails.eventEndDate || 'N/A')],
              ],
              theme: 'grid',
              headStyles: {
                fillColor: '#44566C',
                textColor: '#FFFFFF',
                fontStyle: 'bold',
              },
            });
            finalY = (doc as any).lastAutoTable.finalY + 12;
          }

          const selectedOtherDetails =
            otherDetails?.details?.filter((d) => d.selected) || [];
          if (selectedOtherDetails.length > 0) {
            const body = selectedOtherDetails.map((d) => [
              String(d.questionText || ''),
              String(d.answerText || ''),
            ]);
            const requiredHeight = 20 + body.length * 10;
            checkPageBreak(requiredHeight);

            doc.setFontSize(16);
            doc.text('Other Details', pageMargin, finalY);
            finalY += 7;
            autoTable(doc, {
              startY: finalY,
              head: [['Detail', 'Value']],
              body,
              theme: 'striped',
              headStyles: {
                fillColor: '#44566C',
                textColor: '#FFFFFF',
                fontStyle: 'bold',
              },
            });
            finalY = (doc as any).lastAutoTable.finalY + 12;
          }

          if (sourcingNeeds) {
            Object.keys(sourcingNeeds).forEach((key) => {
              const category = sourcingNeeds[key];
              const selectedSourcing =
                category?.items?.filter((i) => i.selected) || [];
              if (selectedSourcing.length > 0) {
                const body = selectedSourcing.map((item) => [
                  item.questionText,
                  item.answerText,
                ]);
                const requiredHeight = 20 + body.length * 10;
                checkPageBreak(requiredHeight);

                doc.setFontSize(16);
                doc.text(category.label, pageMargin, finalY);
                finalY += 7;
                autoTable(doc, {
                  startY: finalY,
                  head: [['Need', 'Value']],
                  body,
                  theme: 'striped',
                  headStyles: {
                    fillColor: '#44566C',
                    textColor: '#FFFFFF',
                    fontStyle: 'bold',
                  },
                });
                finalY = (doc as any).lastAutoTable.finalY + 12;
              }
            });
          }

          const selectedCompliance =
            compliance?.items?.filter((i) => i.selected) || [];
          if (selectedCompliance.length > 0) {
            const body = selectedCompliance.map((item) =>
              compliance.flexible
                ? [
                    item.name,
                    item.currency,
                    item.amount,
                    item.exchangeRate,
                    item.amountUsd,
                  ]
                : [item.name, item.simpleSelection ? 'Yes' : 'No']
            );
            const requiredHeight = 20 + body.length * 10;
            checkPageBreak(requiredHeight);

            doc.setFontSize(16);
            doc.text('Compliance & Safety', pageMargin, finalY);
            finalY += 7;
            const head = compliance.flexible
              ? [
                  [
                    'Name',
                    'Currency',
                    'Amount',
                    'Exchange Rate',
                    'Amount (USD)',
                  ],
                ]
              : [['Name', 'Required']];
            autoTable(doc, {
              startY: finalY,
              head,
              body,
              theme: 'striped',
              headStyles: {
                fillColor: '#44566C',
                textColor: '#FFFFFF',
                fontStyle: 'bold',
              },
            });
            finalY = (doc as any).lastAutoTable.finalY + 12;
          }
        };

        addContentTables();

        // --- Thank You Page ---
        if (eventDetails?.includeThankYouPage) {
          doc.addPage();

          const thankYouText = 'THANK YOU';
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(40);
          doc.setTextColor('#44566C');
          const thankYouY = pageHeight / 2 - 20;
          doc.text(thankYouText, pageWidth / 2, thankYouY, { align: 'center' });

          const lineWidth = 80;
          const lineY = thankYouY + 10;
          doc.setDrawColor('#44566C');
          doc.setLineWidth(0.5);
          doc.line(
            (pageWidth - lineWidth) / 2,
            lineY,
            (pageWidth + lineWidth) / 2,
            lineY
          );
        }

        doc.save('Event-Details-Formatted.pdf');
        resolve();
      } catch (error) {
        console.error('PDF Generation Service Error:', error);
        reject(error);
      }
    });
  }
}
